import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  IAdminFirmware,
  IFirmwareFormValues,
} from "../../../@types/admin/AdminFirmware";
import { useFileUpload } from "../../../hooks/mutations/admin/fileTransfer";
import { useToast } from "../../../hooks/useToast";
import { buildFirmwareUploadRequest } from "../../../services/admin";
import {
  AdminSelectField,
  CheckboxField,
  FieldError,
  FieldHint,
  FileUploadField,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../shared";
import { GenerationOptions } from "./AdminFirmware.styles";

/** Retool's `deviceInput` options, with its captions. */
const DEVICES = [
  { value: "SpirobankSmart", label: "SpirobankSmart (SDK file)" },
  {
    value: "SpirobankSmartLicense",
    label: "SpirobankSmartLicense (SDK license)",
  },
  { value: "IHealthAirPO3M", label: "IHealth Air PO3M" },
];

/** Retool's `generationsMultiselect` options. */
const GENERATIONS = ["Z0", "Z1"];

/** Retool's `platformsSelect` options. */
const PLATFORMS = [
  { value: "", label: "None" },
  { value: "ios", label: "IOS" },
  { value: "android", label: "Android" },
];

const MAX_BINARY_BYTES = 250 * 1024 * 1024;

interface IFirmwareModalProps {
  /** Undefined creates; a record edits it. */
  firmware?: IAdminFirmware;
  onClose: () => void;
}

/**
 * Adds or updates a firmware record — Retool's `modalAddUpdateFirmware`
 * (EN-5166).
 *
 * ⚠️ Uploading a binary here is what device firmware updates are served
 * from. The version is validated server-side (`Version.Parse`), and the
 * generations list decides which hardware is offered the update — an empty
 * list is not "all", it is "none".
 */
export function FirmwareModal({ firmware, onClose }: IFirmwareModalProps) {
  const { showToast } = useToast();
  const { uploadFile, isUploading, progress } = useFileUpload();
  const queryClient = useQueryClient();

  const isEdit = firmware?.id != null;

  const [values, setValues] = useState<IFirmwareFormValues>({
    device: firmware?.device ?? DEVICES[0].value,
    version: firmware?.version ?? "",
    changeLog: firmware?.changeLog ?? "",
    publish: firmware?.publish ?? false,
    generations: firmware?.generations ?? [],
    platform: firmware?.platform ?? "",
    binary: null,
  });
  const [submitError, setSubmitError] = useState<string>();

  const set = <K extends keyof IFirmwareFormValues>(
    key: K,
    value: IFirmwareFormValues[K]
  ) => setValues((previous) => ({ ...previous, [key]: value }));

  // Retool required device, version and changelog; the binary was only
  // genuinely required on create, since an update keeps the stored file.
  const isValid =
    values.device.length > 0 &&
    values.version.trim().length > 0 &&
    values.changeLog.trim().length > 0 &&
    (isEdit || values.binary !== null);

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitError(undefined);

    const result = await uploadFile(
      buildFirmwareUploadRequest(values, firmware?.id ?? undefined),
      { showErrorToast: false }
    );

    if (result.ok) {
      // The upload goes through useFileUpload, not a query mutation, so the
      // list does not refresh itself — without this the table shows stale
      // rows until the page is remounted (staleTime is 3 minutes).
      queryClient.invalidateQueries({ queryKey: ["admin-firmware"] });
      showToast(
        isEdit ? "The firmware was updated." : "The firmware was created.",
        "success"
      );
      onClose();
      return;
    }
    setSubmitError(result.message);
  };

  const toggleGeneration = (generation: string) => {
    set(
      "generations",
      values.generations.includes(generation)
        ? values.generations.filter((item) => item !== generation)
        : [...values.generations, generation]
    );
  };

  return (
    <ModalContainer>
      <ModalTitle>{isEdit ? "Edit firmware" : "Add firmware"}</ModalTitle>

      <ModalForm>
        <FilterGroup>
          <FilterLabel htmlFor="firmware-device">Device</FilterLabel>
          <AdminSelectField
            id="firmware-device"
            value={values.device}
            disabled={isUploading}
            onChange={(e) => set("device", e.target.value)}
          >
            {DEVICES.map((device) => (
              <option key={device.value} value={device.value}>
                {device.label}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="firmware-version">Version</FilterLabel>
          <FilterInput
            id="firmware-version"
            type="text"
            placeholder="1.0"
            value={values.version}
            disabled={isUploading}
            onChange={(e) => set("version", e.target.value)}
          />
          <FieldHint>
            Must parse as a version number — the API rejects anything else.
          </FieldHint>
        </FilterGroup>

        <FileUploadField
          label="Binary"
          value={values.binary}
          onChange={(file) => set("binary", file)}
          maxBytes={MAX_BINARY_BYTES}
          disabled={isUploading}
          progress={isUploading ? progress : undefined}
          helperText={
            isEdit
              ? "Leave empty to keep the current binary."
              : "The firmware file served to devices."
          }
        />

        {isEdit && firmware?.url && (
          <FieldHint>
            Current binary:{" "}
            <a href={firmware.url} target="_blank" rel="noreferrer">
              {firmware.binary}
            </a>
          </FieldHint>
        )}

        <FilterGroup>
          <FilterLabel htmlFor="firmware-changelog">Changelog</FilterLabel>
          <FilterInput
            id="firmware-changelog"
            type="text"
            value={values.changeLog}
            disabled={isUploading}
            onChange={(e) => set("changeLog", e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Generations</FilterLabel>
          <GenerationOptions>
            {GENERATIONS.map((generation) => (
              <CheckboxField key={generation}>
                <input
                  type="checkbox"
                  checked={values.generations.includes(generation)}
                  disabled={isUploading}
                  onChange={() => toggleGeneration(generation)}
                />
                {generation}
              </CheckboxField>
            ))}
          </GenerationOptions>
          <FieldHint>
            Which hardware generations are offered this firmware. Selecting none
            offers it to none.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="firmware-platform">Platform</FilterLabel>
          <AdminSelectField
            id="firmware-platform"
            value={values.platform}
            disabled={isUploading}
            onChange={(e) => set("platform", e.target.value)}
          >
            {PLATFORMS.map((platform) => (
              <option key={platform.value} value={platform.value}>
                {platform.label}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <CheckboxField>
          <input
            type="checkbox"
            checked={values.publish}
            disabled={isUploading}
            onChange={(e) => set("publish", e.target.checked)}
          />
          Publish
        </CheckboxField>

        {submitError && <FieldError>{submitError}</FieldError>}

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isUploading}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={!isValid || isUploading}
            onClick={handleSubmit}
          >
            {isUploading ? "Saving..." : "Save"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
